import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  Target,
  Activity,
  Users,
  Calendar,
  BarChart3,
  ArrowUp,
  ArrowDown,
  Building2,
  Layers,
  Phone,
  Mail,
  FileText,
  CheckSquare,
  Clock,
  AlertCircle,
  Download,
} from 'lucide-react';
import { getDashboardMetrics, getConsolidatedDashboardMetrics } from '../lib/crmService';
import { getActivityStats, getOverdueTasks } from '../lib/activityService';
import { useBrand } from '../contexts/BrandContext';
import DateRangePicker from './DateRangePicker';
import { getDateRangePreset, DateRange } from '../lib/dateUtils';

export default function Dashboard() {
  const { brands, selectedBrand, setSelectedBrand } = useBrand();
  const [metrics, setMetrics] = useState<any>(null);
  const [consolidatedMetrics, setConsolidatedMetrics] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'brand' | 'consolidated'>('brand');
  const [loading, setLoading] = useState(true);
  const defaultRange = getDateRangePreset('last_30_days');
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);
  const [activityStats, setActivityStats] = useState<any>(null);
  const [overdueTasks, setOverdueTasks] = useState<any[]>([]);

  useEffect(() => {
    if (viewMode === 'brand' && selectedBrand) {
      loadMetrics();
    } else if (viewMode === 'consolidated') {
      loadConsolidatedMetrics();
    }
  }, [selectedBrand, viewMode, dateRange]);

  const loadMetrics = async () => {
    if (!selectedBrand) return;

    try {
      setLoading(true);
      const [metricsData, activityData, overdueData] = await Promise.all([
        getDashboardMetrics(selectedBrand.id, dateRange.startDate, dateRange.endDate),
        getActivityStats(selectedBrand.id, dateRange.startDate, dateRange.endDate),
        getOverdueTasks(selectedBrand.id),
      ]);
      setMetrics(metricsData);
      setActivityStats(activityData);
      setOverdueTasks(overdueData);
    } catch (error) {
      console.error('Error loading dashboard metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConsolidatedMetrics = async () => {
    try {
      setLoading(true);
      const data = await getConsolidatedDashboardMetrics(dateRange.startDate, dateRange.endDate);
      setConsolidatedMetrics(data);
    } catch (error) {
      console.error('Error loading consolidated metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBrandChange = (brandId: string | 'all') => {
    if (brandId === 'all') {
      setViewMode('consolidated');
    } else {
      const brand = brands.find(b => b.id === brandId);
      if (brand) {
        setSelectedBrand(brand);
        setViewMode('brand');
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const [showExportMenu, setShowExportMenu] = useState(false);

  const downloadFile = (filename: string) => {
    const link = document.createElement('a');
    link.href = `/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportFiles = [
    { name: 'src-components.zip', label: 'Components (201KB)', description: 'All React components' },
    { name: 'src-lib.zip', label: 'Libraries (33KB)', description: 'Service libraries and utilities' },
    { name: 'src-other.zip', label: 'Other Source Files (35KB)', description: 'Contexts, hooks, and other files' },
    { name: 'supabase-migrations.zip', label: 'Database (90KB)', description: 'Migrations and functions' },
    { name: 'config-files.zip', label: 'Config Files (43KB)', description: 'Package.json and configs' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const activeMetrics = viewMode === 'consolidated' ? consolidatedMetrics?.overall : metrics;

  if (!activeMetrics) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Unable to load dashboard metrics</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900">Dashboard</h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={setDateRange}
            onApply={() => {}}
          />
          <div className="relative flex-1 sm:flex-none">
            <select
              value={viewMode === 'consolidated' ? 'all' : selectedBrand?.id || ''}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-xs md:text-sm font-medium text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Brands</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <Building2 className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-xs md:text-sm font-medium text-gray-600">Total Opportunities</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">
                {activeMetrics.totalOpportunities}
              </p>
            </div>
            <div className="p-2 md:p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3 md:mt-4 flex items-center text-xs md:text-sm">
            <DollarSign className="w-4 h-4 text-gray-500 mr-1" />
            <span className="text-gray-600">{formatCurrency(activeMetrics.totalValue)} total value</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-xs md:text-sm font-medium text-gray-600">Opportunities Won</p>
              <p className="text-2xl md:text-3xl font-bold text-green-600 mt-2">
                {activeMetrics.wonCount}
              </p>
            </div>
            <div className="p-2 md:p-3 bg-green-100 rounded-lg">
              <Target className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3 md:mt-4 flex items-center text-xs md:text-sm">
            <ArrowUp className="w-4 h-4 text-green-600 mr-1" />
            <span className="text-green-600 font-medium">{formatCurrency(activeMetrics.wonValue)}</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-xs md:text-sm font-medium text-gray-600">Conversion Rate</p>
              <p className="text-2xl md:text-3xl font-bold text-blue-600 mt-2">
                {formatPercent(activeMetrics.conversionRate)}
              </p>
            </div>
            <div className="p-2 md:p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3 md:mt-4 flex items-center text-xs md:text-sm">
            <Activity className="w-4 h-4 text-gray-500 mr-1" />
            <span className="text-gray-600">{activeMetrics.wonCount} won / {activeMetrics.wonCount + activeMetrics.lostCount} closed</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-xs md:text-sm font-medium text-gray-600">Avg Opportunity Value</p>
              <p className="text-2xl md:text-3xl font-bold text-gray-900 mt-2">
                {formatCurrency(activeMetrics.avgOpportunityValue)}
              </p>
            </div>
            <div className="p-2 md:p-3 bg-orange-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-3 md:mt-4 flex items-center text-xs md:text-sm">
            <Calendar className="w-4 h-4 text-gray-500 mr-1" />
            <span className="text-gray-600">{activeMetrics.inProgressCount} in progress</span>
          </div>
        </div>
      </div>

      {viewMode === 'consolidated' && consolidatedMetrics?.byBrand && consolidatedMetrics.byBrand.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Performance by Brand</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {consolidatedMetrics.byBrand.map((brand: any) => (
                <div key={brand.brandId} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-900">{brand.brandName}</h4>
                    </div>
                    <div className="text-xs md:text-xs md:text-sm font-medium text-gray-600">
                      {brand.totalOpportunities} opportunities
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Total Value</p>
                      <p className="text-xs md:text-sm font-semibold text-gray-900">{formatCurrency(brand.totalValue)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Won</p>
                      <p className="text-xs md:text-sm font-semibold text-green-600">{brand.wonCount}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Won Value</p>
                      <p className="text-xs md:text-sm font-semibold text-green-600">{formatCurrency(brand.wonValue)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Conversion</p>
                      <p className="text-xs md:text-sm font-semibold text-blue-600">{formatPercent(brand.conversionRate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">In Progress</p>
                      <p className="text-xs md:text-sm font-semibold text-yellow-600">{brand.inProgressCount}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {viewMode === 'brand' && metrics?.opportunitiesByStage && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Pipeline by Stage</h3>
              <BarChart3 className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {metrics.opportunitiesByStage.map((stage: any, index: number) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-3"
                        style={{ backgroundColor: stage.color }}
                      ></div>
                      <span className="text-xs md:text-sm font-medium text-gray-700">{stage.stage}</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-xs md:text-sm text-gray-600">{stage.count} opps</span>
                      <span className="text-xs md:text-sm font-semibold text-gray-900">
                        {formatCurrency(stage.value)}
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${metrics.totalValue > 0 ? (stage.value / metrics.totalValue) * 100 : 0}%`,
                        backgroundColor: stage.color,
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Summary</h3>
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div>
                <p className="text-xs md:text-sm text-green-800 font-medium">Won Opportunities</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{activeMetrics.wonCount}</p>
              </div>
              <div className="text-right">
                <p className="text-xs md:text-sm text-green-700">{formatCurrency(activeMetrics.wonValue)}</p>
                <div className="flex items-center justify-end mt-1">
                  <ArrowUp className="w-4 h-4 text-green-600 mr-1" />
                  <span className="text-xs text-green-600 font-medium">Revenue</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <div>
                <p className="text-xs md:text-sm text-yellow-800 font-medium">In Progress</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{activeMetrics.inProgressCount}</p>
              </div>
              <div className="text-right">
                <p className="text-xs md:text-sm text-yellow-700">{formatCurrency(activeMetrics.inProgressValue)}</p>
                <div className="flex items-center justify-end mt-1">
                  <Activity className="w-4 h-4 text-yellow-600 mr-1" />
                  <span className="text-xs text-yellow-600 font-medium">Potential</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div>
                <p className="text-xs md:text-sm text-red-800 font-medium">Lost Opportunities</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{activeMetrics.lostCount}</p>
              </div>
              <div className="text-right">
                <p className="text-xs md:text-sm text-red-700">{formatCurrency(activeMetrics.lostValue)}</p>
                <div className="flex items-center justify-end mt-1">
                  <ArrowDown className="w-4 h-4 text-red-600 mr-1" />
                  <span className="text-xs text-red-600 font-medium">Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'brand' && activityStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-blue-600" />
              Activity Overview
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{activityStats.totalActivities}</p>
                <p className="text-xs text-gray-600 mt-1">Total Activities</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {activityStats.activitiesByType.call || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1 flex items-center justify-center">
                  <Phone className="w-3 h-3 mr-1" />
                  Calls
                </p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {activityStats.activitiesByType.email || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1 flex items-center justify-center">
                  <Mail className="w-3 h-3 mr-1" />
                  Emails
                </p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">
                  {activityStats.activitiesByType.meeting || 0}
                </p>
                <p className="text-xs text-gray-600 mt-1 flex items-center justify-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  Meetings
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CheckSquare className="w-5 h-5 mr-2 text-orange-600" />
              Task Management
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 md:p-3 bg-green-50 rounded-lg">
                <div className="flex items-center">
                  <CheckSquare className="w-5 h-5 text-green-600 mr-3" />
                  <span className="text-xs md:text-sm font-medium text-gray-700">Completed Tasks</span>
                </div>
                <span className="text-xl font-bold text-green-600">{activityStats.completedTasks}</span>
              </div>
              <div className="flex items-center justify-between p-2 md:p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-yellow-600 mr-3" />
                  <span className="text-xs md:text-sm font-medium text-gray-700">Pending Tasks</span>
                </div>
                <span className="text-xl font-bold text-yellow-600">{activityStats.pendingTasks}</span>
              </div>
              <div className="flex items-center justify-between p-2 md:p-3 bg-red-50 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
                  <span className="text-xs md:text-sm font-medium text-gray-700">Overdue Tasks</span>
                </div>
                <span className="text-xl font-bold text-red-600">{activityStats.overdueTasks}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'brand' && overdueTasks.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AlertCircle className="w-6 h-6 text-red-600 mr-2" />
            <h3 className="text-lg font-semibold text-red-900">Overdue Tasks</h3>
          </div>
          <div className="space-y-2">
            {overdueTasks.slice(0, 5).map((task) => (
              <div key={task.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{task.title}</p>
                  {task.description && (
                    <p className="text-xs md:text-sm text-gray-600 mt-1">{task.description}</p>
                  )}
                </div>
                <div className="text-xs md:text-sm text-red-600 ml-4">
                  {new Date(task.scheduled_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-2">Welcome to Your CRM Dashboard</h3>
              <p className="text-blue-100">
                {viewMode === 'consolidated'
                  ? 'Track opportunities across all your brands and manage your complete sales pipeline'
                  : `Track your opportunities, manage your pipeline, and close more deals with ${selectedBrand?.name}`
                }
              </p>
            </div>
            <Target className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2">Export Application</h3>
              <p className="text-green-100 mb-4">
                Download application files in separate archives
              </p>
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="flex items-center space-x-2 bg-white text-green-600 px-4 py-2 rounded-lg font-medium hover:bg-green-50 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  <span>Download Files</span>
                </button>

                {showExportMenu && (
                  <div className="absolute left-0 top-full mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-2">
                      {exportFiles.map((file) => (
                        <button
                          key={file.name}
                          onClick={() => {
                            downloadFile(file.name);
                            setShowExportMenu(false);
                          }}
                          className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                        >
                          <div className="flex items-start space-x-3">
                            <Download className="w-5 h-5 text-gray-400 group-hover:text-green-600 mt-0.5" />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">{file.label}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{file.description}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-gray-200 p-3 bg-gray-50 rounded-b-lg">
                      <p className="text-xs text-gray-600">
                        Files are in .zip format. Double-click to extract.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <Download className="w-12 h-12 text-green-200" />
          </div>
        </div>
      </div>
    </div>
  );
}
